import React, { useState, useMemo, useEffect } from 'react';
import { Link } from "react-router";

import * as OPTIONS from "../option-fields";
import { getBasketData } from "../router";

import InstanceEventSummary from '../components/instance-event-summary';
import InstanceBasketSummary from '../components/instance-basket-summary';
import BackButton from '../components/back-button';
import MultibuyIcon from '../svg/multibuy';

const getBatchSize = () => window.innerWidth >= 992 ? 6 : 4;

export default function MultiBuy() {
    const [batchSize, setBatchSize] = useState(getBatchSize);
    const [visibleCount, setVisibleCount] = useState(getBatchSize);

    useEffect(() => {
        const handleResize = () => setBatchSize(getBatchSize());
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { data: basketData } = getBasketData();

    // Spektrix event names are often "Title - Presenter"; normalise to just the title part
    // so they match the shorter WordPress card titles (e.g. "The Marriage of Figaro").
    const basketEventNames = useMemo(() => {
        if (!basketData?.tickets) return new Set();
        const names = new Set();
        basketData.tickets.forEach(ticket => {
            const event = ticket.event?.name ? ticket.event : ticket.instance?.event;
            if (event?.name) {
                names.add(event.name.toLowerCase().trim().split(' - ')[0].trim());
            }
        });
        return names;
    }, [basketData]);

    const currentPath = window.location.pathname;

    const filteredCards = useMemo(() => {
        return OPTIONS.multibuyShowsCards.filter(card => {
            // Always exclude the currently booked event by matching its URL path
            if (card.eventPath && currentPath.startsWith(card.eventPath.replace(/\/$/, ''))) {
                return false;
            }
            // Exclude events already in the basket — normalise card title the same way
            const cardTitle = card.title.toLowerCase().trim().split(' - ')[0].trim();
            if (basketEventNames.size && basketEventNames.has(cardTitle)) {
                return false;
            }
            return true;
        });
    }, [basketEventNames]);

    const visibleCards = filteredCards.slice(0, visibleCount);
    const hasMore = visibleCount < filteredCards.length;

    const multibuyCount = useMemo(() => {
        if (!basketData?.tickets) return 1;
        const instanceIds = new Set();
        basketData.tickets.forEach(ticket => {
            const event = ticket.event?.name ? ticket.event : ticket.instance?.event;
            if (event?.attribute_MultiBuy) {
                instanceIds.add(ticket.instance?.id);
            }
        });
        return Math.max(1, instanceIds.size);
    }, [basketData]);

    const discountDescription = multibuyCount >= 3
        ? OPTIONS.multibuyOffer.descriptionThree
        : multibuyCount === 2
            ? OPTIONS.multibuyOffer.descriptionTwo
            : OPTIONS.multibuyOffer.descriptionOne;

    return (
        <div className='booking-pathway__container booking-pathway__multibuy'>
            <InstanceEventSummary />

            <div className='booking-pathway__main-content'>
                <div className='extras-group'>
                    {OPTIONS.multibuyOffer.title && (
                        <h2 className='extras-group__title'
                            dangerouslySetInnerHTML={{ __html: OPTIONS.multibuyOffer.title.innerHTML }}
                        />
                    )}
                    {discountDescription && (
                        <div className='extras-group__description'
                            dangerouslySetInnerHTML={{ __html: discountDescription.innerHTML }}
                        />
                    )}
                </div>

                <div className="multibuy-extras">
                    <div className="multibuy-extras__title">
                        <MultibuyIcon />
                        {OPTIONS.multibuyOffer.showsTitle && (
                            <h2 dangerouslySetInnerHTML={{ __html: OPTIONS.multibuyOffer.showsTitle.innerHTML }} />
                        )}
                    </div>

                    {visibleCards.length > 0 && (
                        <>
                            <div className="multibuy-extras__shows grid">
                                {visibleCards.map((card, i) => (
                                    <article key={i} className={card.className} dangerouslySetInnerHTML={{ __html: card.innerHTML }} />
                                ))}
                            </div>
                            {hasMore && (
                                <button
                                    className="btn btn--load"
                                    type="button"
                                    onClick={() => setVisibleCount(c => c + batchSize)}
                                >
                                    Show more
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect x="3.99951" y="12.6668" width="1.33332" height="15.9999" transform="rotate(-90 3.99951 12.6668)" fill="#F6F9ED"/>
                                        <rect x="12.6675" y="20" width="1.33332" height="15.9999" transform="rotate(-180 12.6675 20)" fill="#F6F9ED"/>
                                    </svg>
                                </button>
                            )}
                        </>
                    )}

                </div>
            </div>



            <InstanceBasketSummary />

            <div className='booking-pathway__footer'>
                <Link to="/basket" className='btn btn--primary' onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Continue</Link>
                <BackButton />
            </div>
        </div>
    );
}
